import { createSignal, Show } from 'solid-js'
import { KeyframeDiamond } from '@/components/Timeline/KeyframeDiamond'
import { useChangeHistory } from '@/contexts/ChangeHistoryContext'
import { useKeyframeTarget } from '@/contexts/KeyframeTargetContext'
import { useTimeline } from '@/contexts/TimelineContext'
import { keyframeChangedParams, keyframeEditedParam, } from '@/utils/keyframeOnChange'
import ui from './ScrubInput.module.css'

type ScrubInputProps = {
  label: string
  value: number
  min?: number
  max?: number
  step?: number
  onInput: (value: number) => void
  formatValue?: (value: number) => string
  dataParameterPath?: string
  'data-tour-target'?: string
  /** Dims the control and blocks scrub/edit — used when a setting has no effect. */
  disabled?: boolean
  /** Tooltip explaining why the control is disabled. */
  disabledReason?: string
}

export function ScrubInput(props: ScrubInputProps) {
  const timeline = useTimeline()
  const history = useChangeHistory()
  const { selectedKeyframePath } = useKeyframeTarget()
  const [editing, setEditing] = createSignal(false)
  const [editValue, setEditValue] = createSignal('')
  let inputRef: HTMLInputElement | undefined

  const step = () => props.step ?? 0.01
  const formatValue = () =>
    props.formatValue
      ? props.formatValue(props.value)
      : Number.isInteger(step())
        ? String(Math.round(props.value))
        : props.value.toFixed(3)

  function startScrub(e: PointerEvent) {
    if (props.disabled || editing()) return
    const startX = e.clientX
    const startValue = props.value
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    // Batch the whole scrub into one history entry (like Slider): without a
    // preview every pointer-move recorded its own entry, so undoing a drag
    // rewound it pixel by pixel.
    history.startPreview(`Edit ${props.label}`)

    function onMove(ev: PointerEvent) {
      const dx = ev.clientX - startX
      const sensitivity = ev.shiftKey ? 0.1 : 1
      let newValue = startValue + dx * step() * sensitivity
      if (props.min !== undefined) newValue = Math.max(props.min, newValue)
      if (props.max !== undefined) newValue = Math.min(props.max, newValue)
      props.onInput(newValue)

      keyframeEditedParam(timeline, props.dataParameterPath)
    }

    function onUp() {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      history.commit()
      // End of gesture: the next scrub must be its own undo step.
      timeline?.breakUndoCoalescing()
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
  }

  function startEdit() {
    if (props.disabled) return
    setEditValue(formatValue())
    setEditing(true)
    requestAnimationFrame(() => inputRef?.select())
  }

  function commitEdit() {
    const parsed = parseFloat(editValue())
    if (!isNaN(parsed)) {
      let val = parsed
      if (props.min !== undefined) val = Math.max(props.min, val)
      if (props.max !== undefined) val = Math.min(props.max, val)
      props.onInput(val)
      // Typed edits count as changes for the track-changes diamond too.
      if (props.dataParameterPath) {
        keyframeChangedParams(timeline, [props.dataParameterPath])
      }
    }
    setEditing(false)
  }

  function cancelEdit() {
    setEditing(false)
  }

  return (
    <label
      class={ui.label}
      classList={{
        [ui.disabled as string]: props.disabled === true,
        [ui.targeted as string]:
          props.dataParameterPath !== undefined &&
          selectedKeyframePath() === props.dataParameterPath,
      }}
      title={props.disabled ? props.disabledReason : undefined}
      data-tour-target={props['data-tour-target']}
      data-parameter-path={props.dataParameterPath}
      data-step={step()}
      data-min={props.min}
      data-max={props.max}
      onDblClick={startEdit}
      onPointerDown={startScrub}
      onContextMenu={(e) => {
        e.preventDefault()
      }}
    >
      <Show when={props.dataParameterPath && timeline}>
        <KeyframeDiamond parameterPath={props.dataParameterPath!} />
      </Show>
      <span class={ui.labelName}>{props.label}</span>
      <Show
        when={editing()}
        fallback={<span class={ui.value}>{formatValue()}</span>}
      >
        <input
          ref={inputRef}
          class={ui.input}
          type="text"
          value={editValue()}
          onInput={(e) => setEditValue(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitEdit()
            if (e.key === 'Escape') cancelEdit()
            e.stopPropagation()
          }}
          onBlur={commitEdit}
          onClick={(e) => {
            e.stopPropagation()
          }}
          onPointerDown={(e) => {
            e.stopPropagation()
          }}
        />
      </Show>
    </label>
  )
}
